/**
 * Shared between Organization and Staff views.
 * Not shared with Provider views (Provider Connections is a separate implementation).
 */

import { useInfiniteQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ConnectionsService, SourceTypeEnum } from '../../../../../js/api';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { getConnectionSenderName } from '../../../../../js/app/utils/connections';
import { Badge } from '../../../components/badge';
import { Drawer, DrawerBody } from '../../../components/drawer';
import { Heading } from '../../../components/heading';
import { ListErrorState } from '../../../components/list-error-state';
import { BooleanFilterConfig, ListFilter } from '../../../components/list-filter';
import { QueueList } from '../../../components/queue-list';
import { StatusPill } from '../../../components/StatusPill';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSortHeader,
} from '../../../components/table';
import { QueueCodes, WorkflowScopes } from '../../../constants/workflows';
import { useCodeDefinitionsAsMap } from '../../../hooks/useCodeDefinitions';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useListFilterParams } from '../../../hooks/useListFilterParams';
import {
  getPcfStatusFlag,
  PCF_FLAG_BADGES,
  SOURCE_TYPE_ORGANIZATION_BADGE_STYLE,
} from '../../../utils/dataStyles';
import { formatDate } from '../../../utils/date';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import { STICKY_HEADER } from '../../../utils/globalStyles';
import { MAIN_CONTENT_AREA, PAGE_HEADER } from '../../../utils/layoutStyles';
import { TRANSITION_ALL } from '../../../utils/transitionStyles';
import { ConnectionDrawer } from '../../Organization/Connections/ConnectionDrawer';
import { ConnectionsCard } from '../../Organization/Connections/ConnectionsCard';

const DATE_FORMAT = 'LLL d, yyyy';
const PAGE_SIZE = 20;
// Year-aware list dates without the day-of-week ("Sep 6" / "Feb 26, 2024").
const LIST_DATE_PATTERN = 'MMM d';

const QUEUE_CATEGORY = 'HealthcareEntityConnectionQueueNames';

type ApiParams = Parameters<typeof ConnectionsService.connectionsConnectedFacilityList>[0];

const CATEGORY_TO_API_PARAM: Record<string, keyof ApiParams> = {
  Specialty: 'specialties',
};

const CATEGORIES = Object.keys(CATEGORY_TO_API_PARAM) as readonly string[];

export type ConnectionsListPageProps = {
  title?: string;
  queryKeyBase: string;
  displayTypePrefKey: string;
  showOrganization: boolean;
  showOrganizationFilter?: boolean;
  linkContext?: 'organization' | 'staff';
};

// eslint-disable-next-line sonarjs/cognitive-complexity
export const ConnectionsListPage: React.FC<ConnectionsListPageProps> = ({
  title = 'Connections',
  queryKeyBase,
  displayTypePrefKey,
  showOrganization,
  showOrganizationFilter = false,
  linkContext = 'organization',
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  useDocumentTitle(title);
  const [searchParams, setSearchParams] = useSearchParams();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const selectedQueueCode: string | undefined = searchParams.get('queue') || undefined;

  const isStaffView = linkContext === 'staff';
  // Staff-only checkboxes: "Needs PCF" = requiring enrollment, far enough
  // along (AWAIT_PRVLG / ACTIVE), and not yet enrolled; "Requires PCF" is a
  // straight match on the requirement flag.
  const booleanFiltersConfig = useMemo<BooleanFilterConfig[]>(
    () =>
      isStaffView
        ? [
            {
              trueLabel: 'Needs PCF',
              falseLabel: '',
              queryString: 'needs_pcf',
              defaultValue: false,
              renderAs: 'checkbox',
            },
            {
              trueLabel: 'Requires PCF',
              falseLabel: '',
              queryString: 'require_pcf',
              defaultValue: false,
              renderAs: 'checkbox',
            },
          ]
        : [],
    [isStaffView]
  );

  const { filterValues, updateFilters } = useListFilterParams(searchParams, setSearchParams, {
    categories: CATEGORIES,
    displayTypePrefKey,
    booleanFilters: booleanFiltersConfig,
  });

  const search = filterValues.search || '';
  const statuses = filterValues.statuses || [];
  const reasons = filterValues.reasons || [];
  const categoryFilters = useMemo(() => filterValues.categories || {}, [filterValues.categories]);
  const healthcareEntity = filterValues.healthcare_entity || '';
  const needsPcf = (filterValues.booleanFilters?.needs_pcf ?? false) && isStaffView;
  const requirePcf = (filterValues.booleanFilters?.require_pcf ?? false) && isStaffView;

  const displayType = (filterValues.displayType || 'table') as 'card' | 'table';
  const ordering = (filterValues.ordering || '-updated_at') as string;

  const setOrdering = useCallback(
    (next: string) => {
      updateFilters({
        search: filterValues.search,
        statuses: filterValues.statuses,
        reasons: filterValues.reasons,
        categories: filterValues.categories,
        booleanFilters: filterValues.booleanFilters,
        displayType,
        ordering: next,
        healthcare_entity: filterValues.healthcare_entity,
      });
    },
    [
      updateFilters,
      filterValues.search,
      filterValues.statuses,
      filterValues.reasons,
      filterValues.categories,
      filterValues.booleanFilters,
      filterValues.healthcare_entity,
      displayType,
    ]
  );

  const { data: specialtyMap = {} } = useCodeDefinitionsAsMap('Specialty');

  const handleQueueChange = (queueCode: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (queueCode) newSearchParams.set('queue', queueCode);
    else newSearchParams.delete('queue');
    setSearchParams(newSearchParams, { replace: true });
  };

  const apiQueueCode = selectedQueueCode ? (selectedQueueCode as ApiParams['queue']) : undefined;

  const categoryApiParams = useMemo(() => {
    const params: Partial<ApiParams> = {};
    Object.entries(categoryFilters).forEach(([category, values]) => {
      const apiParamName = CATEGORY_TO_API_PARAM[category];
      if (apiParamName && values.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (params as Record<string, unknown>)[apiParamName] = values;
      }
    });
    return params;
  }, [categoryFilters]);

  const queryKey = useMemo(
    () => [
      queryKeyBase,
      selectedQueueCode,
      search,
      statuses,
      reasons,
      categoryFilters,
      ordering,
      healthcareEntity,
      needsPcf,
      requirePcf,
    ],
    [
      queryKeyBase,
      selectedQueueCode,
      search,
      statuses,
      reasons,
      categoryFilters,
      ordering,
      healthcareEntity,
      needsPcf,
      requirePcf,
    ]
  );

  const {
    data,
    isLoading: isLoadingConnections,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      return ConnectionsService.connectionsConnectedFacilityList({
        queue: apiQueueCode,
        search: search || undefined,
        status: statuses.length > 0 ? (statuses as Array<string | null>) : undefined,
        reason: reasons.length > 0 ? (reasons as Array<string | null>) : undefined,
        healthcareEntity: showOrganization ? healthcareEntity || undefined : undefined,
        needsPcf: needsPcf || undefined,
        requirePcf: requirePcf || undefined,
        ...categoryApiParams,
        ordering: ordering as ApiParams['ordering'],
        limit: PAGE_SIZE,
        offset: pageParam as number,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.next) return allPages.length * PAGE_SIZE;
      return undefined;
    },
    enabled: !!selectedQueueCode,
  });

  const connections = useMemo(
    () => data?.pages.flatMap((page) => page.results || []) || [],
    [data]
  );
  const totalCount = data?.pages[0]?.count || 0;

  const loadMoreConnections = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) await fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadingRef } = useInfiniteScroll(loadMoreConnections, hasNextPage || false, {
    enabled: hasNextPage && !isLoadingConnections,
  });

  useEffect(() => {
    const connectionIdFromUrl = searchParams.get('id');
    if (connectionIdFromUrl) setIsDrawerOpen(true);
    else if (isDrawerOpen) setIsDrawerOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleRowClick = useCallback(
    (connectionId: string) => {
      setIsDrawerOpen(true);
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('id', connectionId);
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('id');
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const selectedIdFromUrl = searchParams.get('id');

  const selectedIndex = useMemo(() => {
    if (!selectedIdFromUrl) return -1;
    return connections.findIndex((c) => String(c.id) === String(selectedIdFromUrl));
  }, [connections, selectedIdFromUrl]);

  const prevConnectionId: string | null = useMemo(() => {
    if (selectedIndex > 0) return connections[selectedIndex - 1]?.id ?? null;
    return null;
  }, [connections, selectedIndex]);

  const nextConnectionId: string | null = useMemo(() => {
    if (selectedIndex >= 0 && selectedIndex < connections.length - 1)
      return connections[selectedIndex + 1]?.id ?? null;
    return null;
  }, [connections, selectedIndex]);

  const neighborsByIdRef = useRef<Map<string, { prev: string | null; next: string | null }>>(
    new Map()
  );

  useEffect(() => {
    if (!selectedIdFromUrl) return;
    if (selectedIndex >= 0) {
      neighborsByIdRef.current.set(String(selectedIdFromUrl), {
        prev: prevConnectionId ?? null,
        next: nextConnectionId ?? null,
      });
    }
  }, [selectedIdFromUrl, selectedIndex, prevConnectionId, nextConnectionId]);

  const cachedNeighbors = selectedIdFromUrl
    ? neighborsByIdRef.current.get(String(selectedIdFromUrl))
    : undefined;
  const drawerPrevId: string | null =
    selectedIndex >= 0 ? prevConnectionId : (cachedNeighbors?.prev ?? null);
  const drawerNextId: string | null =
    selectedIndex >= 0 ? nextConnectionId : (cachedNeighbors?.next ?? null);

  const initialIdRef = useRef<string | null>(searchParams.get('id'));

  // Error must render distinctly from "no results" — a failed query (e.g. an
  // expired session's 403) previously collapsed into the empty state and read
  // as "No connections found".
  const listStateContent = isError ? (
    <ListErrorState testId="connections-error" />
  ) : isLoadingConnections && connections.length === 0 ? (
    <div className="tw:text-center tw:py-8" data-testid="connections-loading">
      Loading connections...
    </div>
  ) : connections.length === 0 ? (
    <div className="tw:text-center tw:text-zinc-500 tw:py-8" data-testid="connections-empty">
      No connections found
      <div className="tw:italic tw:text-zinc-500 tw:text-sm">
        Refine search criteria or check back later
      </div>
    </div>
  ) : null;

  useEffect(() => {
    if (!selectedQueueCode || selectedQueueCode === 'all') return;
    if (!selectedIdFromUrl) return;

    if (initialIdRef.current && selectedIdFromUrl === initialIdRef.current) return;

    if (selectedIndex === -1 && !isLoadingConnections) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('id');
      nextParams.delete('tab');
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    selectedQueueCode,
    selectedIdFromUrl,
    selectedIndex,
    isLoadingConnections,
    searchParams,
    setSearchParams,
  ]);

  return (
    <>
      <div
        className="tw:flex tw:h-[calc(100vh-72px)] tw:overflow-hidden"
        data-testid="connections-list"
      >
        <div
          className={clsx(
            `tw:flex tw:flex-col ${TRANSITION_ALL} tw:overflow-hidden`,
            isDrawerOpen ? 'tw:w-3/5 tw:lg:w-3/5' : 'tw:w-full'
          )}
        >
          <main className={MAIN_CONTENT_AREA}>
            <div className={STICKY_HEADER}>
              <div className={PAGE_HEADER}>
                <Heading level={1} className="tw:!text-brand-green-0">
                  <i className="far fa-fw fa-handshake tw:mr-1" />
                  {title}
                </Heading>
              </div>

              <div className="tw:mt-4">
                <QueueList
                  categoryName={QUEUE_CATEGORY}
                  notificationScope={WorkflowScopes.ConnectedFacility}
                  selectedQueue={selectedQueueCode || undefined}
                  priorityQueues={true}
                  priorityQueueList={[QueueCodes.FOR_REVIEW, QueueCodes.TO_CONFIRM]}
                  onQueueChange={handleQueueChange}
                />
              </div>
            </div>

            <div className="tw:mt-1">
              <div className="tw:group" data-drawer-open={isDrawerOpen ? 'true' : 'false'}>
                <ListFilter
                  show_search={true}
                  workflow_scope={WorkflowScopes.ConnectedFacility}
                  categories={Array.from(CATEGORIES)}
                  showStatus={true}
                  showReason={true}
                  showOrganizationFilter={showOrganizationFilter}
                  showDisplayType={true}
                  booleanFilters={booleanFiltersConfig}
                  sortOptions={[
                    ...(showOrganization
                      ? [{ label: 'Organization', value: 'healthcare_entity__name' }]
                      : []),
                    { label: 'Last Updated', value: 'updated_at' },
                    { label: 'Created', value: 'connected_at' },
                    { label: 'Provider', value: 'provider_display_name' },
                    { label: 'Facility', value: 'facility__name' },
                    { label: 'Location', value: 'facility__city' },
                    { label: 'Status', value: 'current_status__title' },
                    { label: 'Sent By', value: 'healthcare_entity_rep_name' },
                    { label: 'First Shift', value: 'first_shift' },
                    { label: 'Last Shift', value: 'last_shift' },
                    ...(isStaffView ? [{ label: 'Last Note', value: 'last_note_at' }] : []),
                  ]}
                  values={filterValues}
                  onFilterChange={updateFilters}
                />
              </div>
            </div>

            <div className="tw:mt-6">
              {listStateContent ?? (
                <>
                  {connections.length > 0 && (
                    <div className="tw:text-right tw:text-xs tw:text-zinc-500 tw:my-1 tw:mr-1">
                      Showing {connections.length} of {totalCount} connections
                    </div>
                  )}

                  {displayType === 'card' ? (
                    <div className="tw:@container">
                      <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:@min-[513px]:grid-cols-2 tw:@min-[793px]:grid-cols-3">
                        {connections.map((connection) => {
                          const connectionIdFromUrl = searchParams.get('id');
                          const isSelected = connection.id === connectionIdFromUrl;

                          return (
                            <button
                              key={connection.id}
                              type="button"
                              onClick={() => handleRowClick(connection.id)}
                              className={clsx('tw:cursor-pointer tw:block tw:w-full tw:text-left')}
                              data-testid={`connection-card-${connection.id}`}
                            >
                              <ConnectionsCard
                                connection={connection}
                                specialtyMap={specialtyMap}
                                isSelected={isSelected}
                                showOrganization={showOrganization}
                                showPcfStatus={isStaffView}
                              />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <Table striped dense>
                      <TableHead>
                        <TableSortHeader
                          sortKey="provider_display_name"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Provider
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="facility__name"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Facility Name
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="facility__city"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Location
                        </TableSortHeader>
                        {showOrganization && (
                          <TableSortHeader
                            sortKey="healthcare_entity__name"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Organization
                          </TableSortHeader>
                        )}
                        <TableHeader>Specialty</TableHeader>
                        <TableHeader>Opportunity</TableHeader>
                        <TableSortHeader
                          sortKey="current_status__title"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Status
                        </TableSortHeader>
                        {selectedQueueCode === 'all' && <TableHeader>Reason</TableHeader>}
                        <TableSortHeader
                          sortKey="healthcare_entity_rep_name"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Sent By
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="first_shift"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          First Shift
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="last_shift"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Last Shift
                        </TableSortHeader>
                        {isStaffView && (
                          <TableSortHeader
                            sortKey="last_note_at"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Last Note
                          </TableSortHeader>
                        )}
                        <TableSortHeader
                          sortKey="updated_at"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Last Updated
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="connected_at"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Created
                        </TableSortHeader>
                      </TableHead>
                      <TableBody>
                        {connections.map((connection) => {
                          const specialtyText =
                            connection.opportunity?.specialty_display ||
                            (connection.provider?.specialties || []).join(', ') ||
                            '-';

                          const connectionIdFromUrl = searchParams.get('id');
                          const isSelected = connection.id === connectionIdFromUrl;
                          const pcfStatusFlag = isStaffView ? getPcfStatusFlag(connection) : null;

                          return (
                            <TableRow
                              key={connection.id}
                              onClick={() => handleRowClick(connection.id)}
                              className={clsx(isSelected ? 'tw:!bg-brand-green-2' : '')}
                              data-testid={`connection-row-${connection.id}`}
                            >
                              <TableCell>
                                {connection.provider.display_name}
                                {connection.source_type === SourceTypeEnum.ORGANIZATION && (
                                  <span
                                    className={clsx(
                                      'tw:ml-2',
                                      SOURCE_TYPE_ORGANIZATION_BADGE_STYLE
                                    )}
                                  >
                                    1099
                                  </span>
                                )}
                              </TableCell>
                              <TableCell>{connection.facility.name}</TableCell>
                              <TableCell>
                                {getFacilityLocationDisplayValue(connection.facility) || '--'}
                              </TableCell>
                              {showOrganization && (
                                <TableCell>{connection.healthcare_entity?.name || '--'}</TableCell>
                              )}
                              <TableCell>{specialtyText}</TableCell>
                              <TableCell>{connection.opportunity?.code || '--'}</TableCell>
                              <TableCell>
                                <div className="tw:flex tw:flex-col tw:items-start tw:gap-1">
                                  <StatusPill
                                    scope={WorkflowScopes.ConnectedFacility}
                                    status={connection.current_status}
                                  />
                                  {pcfStatusFlag && (
                                    <Badge
                                      color={PCF_FLAG_BADGES[pcfStatusFlag].color}
                                      data-testid={`connection-row-pcf-${connection.id}`}
                                    >
                                      {PCF_FLAG_BADGES[pcfStatusFlag].label}
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>
                              {selectedQueueCode === 'all' && (
                                <TableCell>{connection.current_reason?.title || '--'}</TableCell>
                              )}
                              <TableCell>{getConnectionSenderName(connection)}</TableCell>
                              <TableCell>
                                {formatDate(connection.first_shift, LIST_DATE_PATTERN)}
                              </TableCell>
                              <TableCell>
                                {formatDate(connection.last_shift, LIST_DATE_PATTERN)}
                              </TableCell>
                              {isStaffView && (
                                <TableCell
                                  data-testid={`connection-row-last-note-${connection.id}`}
                                >
                                  {formatDate(connection.last_note_at, LIST_DATE_PATTERN)}
                                </TableCell>
                              )}
                              <TableCell>
                                {connection.updated_at
                                  ? format(parseISO(connection.updated_at), DATE_FORMAT)
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                {connection.connected_at
                                  ? format(parseISO(connection.connected_at), DATE_FORMAT)
                                  : '-'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}

                  {hasNextPage && (
                    <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
                      {isFetchingNextPage && (
                        <div className="tw:text-center tw:text-zinc-500">
                          Loading more connections...
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </main>
        </div>

        <Drawer open={isDrawerOpen} onClose={handleCloseDrawer} width="3xl" mode="side-by-side">
          <DrawerBody className="tw:!text-sm/6">
            {selectedIdFromUrl && (
              <ConnectionDrawer
                key={selectedIdFromUrl}
                connectionId={selectedIdFromUrl}
                prevId={drawerPrevId}
                nextId={drawerNextId}
                selectedQueueCode={selectedQueueCode}
                onClose={handleCloseDrawer}
                linkContext={linkContext}
              />
            )}
          </DrawerBody>
        </Drawer>
      </div>
    </>
  );
};
